import classNames from 'classnames'
import * as React from 'react'

import Heading from 'components/Heading'
import Photo from 'components/Photo'
import { PhotoSrcProps } from 'components/Photo/Photo'
import Video from 'components/Video'

import { Button, TYPE } from 'components/Button'
import { Col, Grid, GridGap } from 'components/Grid'
import { DisplayContent as DefaultContent } from 'react-content-engine'
import './Hero.scss'

export enum HeroType {
  funnel = 'funnel',
  entries = 'entries',
  cardEntries = 'card-entries',
  specials = 'specials',
  slim = 'slim',
  tallSlim = 'tall-slim',
  entry = 'entry',
  logo = 'logo',
  logoTall = 'logo-tall',
  flavourEntry = 'flavour-entry',
  flatImage = 'flat-image',
  cardEntriesPlain = 'card-entries-plain',
}
interface HeroProps extends PhotoSrcProps {
  type: HeroType
  headingText: string | JSX.Element
  subHeading?: string | JSX.Element
  headingColor?: 'black' | 'white'
  underHeading?: JSX.Element
  cardEntries?: JSX.Element
  size?: number
  video?: boolean
  vignet?: boolean
  goToTripsHandler?: () => void
  children?: React.ReactNode | undefined
}

const Hero: React.FC<HeroProps> = props => {
  const isAlternateEntries = props.type === HeroType.cardEntriesPlain
  const renderEntries = () => {
    return (
      <div className="wrapper-lower">
        <div className={`wrapper-background`}>
          <Photo
            alt={props.alt || ``}
            src={props.src}
            src768={props.src768}
            src1024={props.src1024}
            src1280={props.src1280}
            src1600={props.src1600}
            src1920={props.src1920}
            vignet={props.vignet}
            firstName={props.firstName}
            lastName={props.lastName}
          />
        </div>
        <div className={`wrapper-show-more-link`}>
          <Grid
            nrCols={isAlternateEntries ? [1, 1, 1, 1] : [1, 2, 2, 2]}
            colGap={[GridGap.m]}
            rowGap={[GridGap.l]}
          >
            <Col className={`show-more-label`}>
              <p className="show-more-label-title">
                <DefaultContent contentKey="go-to-themes-label">
                  OF GA VOOR EEN SPECIFIEK THEMA:
                </DefaultContent>
              </p>
            </Col>
            <Col>
              {!isAlternateEntries && (
                <Button type={TYPE.inlinetext} onClick={props.goToTripsHandler}>
                  <DefaultContent contentKey="show-all-trips-link">
                    Toon alle srprs.trips
                  </DefaultContent>
                </Button>
              )}
            </Col>
          </Grid>
        </div>
        <div className="wrapper-entry">
          <Grid
            nrCols={[1, 2, 3, 3]}
            slider
            colGap={[GridGap.m]}
            className="srprs-cardEntry-wrapper"
          >
            {props.cardEntries}
          </Grid>
        </div>
        <div className={`wrapper-show-more-button`}>
          <p>
            <Button
              type={isAlternateEntries ? TYPE.plain : TYPE.primary}
              {...(isAlternateEntries ? { cutOff: 'none' } : {})}
              onClick={props.goToTripsHandler}
            >
              <DefaultContent contentKey="show-all-trips-button">
                BEKIJK ALLE SRPRS.TRIPS
              </DefaultContent>
            </Button>
          </p>
        </div>
      </div>
    )
  }

  const renderVisual = () => {
    const {
      src,
      src768,
      src1024,
      src1280,
      src1600,
      src1920,
      video,
      vignet,
      firstName,
      lastName,
      alt,
      blurMobile,
    } = props

    return (
      <div className="wrapper-visual">
        <div className="wrapper-inner">
          {video === true ? (
            <Video
              alt={alt || ``}
              src={src}
              src768={src768}
              src1024={src1024}
              src1280={src1280}
              src1600={src1600}
              src1920={src1920}
            />
          ) : (
            <Photo
              alt={alt || ``}
              src={src}
              src768={src768}
              src1024={src1024}
              src1280={src1280}
              src1600={src1600}
              src1920={src1920}
              vignet={vignet}
              firstName={firstName}
              lastName={lastName}
              blurMobile={blurMobile}
            />
          )}
        </div>
      </div>
    )
  }

  const renderIntro = () => {
    const { size, subHeading, headingText, underHeading } = props

    return (
      <div className="wrapper-intro">
        <div className="wrapper-inner">
          <Heading
            alternate={isAlternateEntries}
            level={size}
            subHeading={subHeading}
          >
            {headingText}
          </Heading>
          {underHeading ? (
            <div className="under-heading">{underHeading}</div>
          ) : null}
        </div>
      </div>
    )
  }

  const className = classNames(
    'srprs-hero',
    'type-' + props.type,
    props.type === 'flavour-entry' ? 'type-entry' : '',
    props.headingColor ? 'colour-' + props.headingColor : '',
  )

  const showVisual = [
    HeroType.specials,
    HeroType.tallSlim,
    HeroType.slim,
    HeroType.entry,
    HeroType.logo,
    HeroType.logoTall,
    HeroType.flavourEntry,
    HeroType.flatImage,
  ].includes(props.type)
  const showIntro = ![
    HeroType.entry,
    HeroType.logo,
    HeroType.logoTall,
    HeroType.flavourEntry,
  ].includes(props.type)
  const showEntries = [
    HeroType.entries,
    HeroType.cardEntries,
    HeroType.cardEntriesPlain,
  ].includes(props.type)

  return (
    <header className={className}>
      {showVisual && renderVisual()}
      {showIntro && renderIntro()}

      <div style={{ position: 'relative' }}>
        <div className="wrapper-highlight">
          <div className="wrapper-inner">{props.children}</div>
        </div>
        {props.type === HeroType.funnel ? renderVisual() : null}
        {showEntries && renderEntries()}
      </div>
    </header>
  )
}

Hero.defaultProps = {
  type: HeroType.funnel,
  size: 1,
  vignet: true,
}

export default Hero
